import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { auth, firestore } from '@tabletuck/firebase-services';
import {
  setUser,
  setDeliveryStore,
  userAddresses,
} from '@tabletuck/redux-slices';

/**
 * Unified hook for managing delivery address state across the app.
 * Syncs address selection between ChangeBranchMenu, CartDrawer, and Redux.
 *
 * Handles:
 * - Anonymous users (stores address in Redux only)
 * - Logged-in users (syncs to Firestore + Redux)
 * - Adding new addresses
 * - Switching between addresses
 * - Converting location data to delivery address format
 */
export const useDeliveryAddress = () => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.authReducer.userData);
  const addresses = useSelector((state) => state.authReducer.userAddresses);
  const deliveryStore = useSelector((state) => state.shopReducer.deliveryStore);
  const deliveryAddress = useSelector(
    (state) => state.shopReducer.deliveryAddress
  );

  // Check if user is anonymous - compute fresh each time, don't memoize with empty deps
  // This ensures we always have the current auth state
  // CRITICAL: Include auth.currentUser?.uid in deps so it updates when user authenticates
  const isAnonymous = useMemo(() => {
    const currentIsAnonymous = auth?.currentUser?.isAnonymous ?? true;
    return currentIsAnonymous;
  }, [auth?.currentUser?.uid, auth?.currentUser?.isAnonymous]); // Re-compute when user changes

  // Get current delivery address ID
  // Priority: branchMenu address (ALWAYS WINS) > deliveryStore > user's default > first address
  const currentDeliveryAddressId = useMemo(() => {
    // deliveryStore is now an object with { address, store, addressData, source }
    const storeAddress =
      typeof deliveryStore === 'object'
        ? deliveryStore?.address
        : deliveryStore;
    const source =
      typeof deliveryStore === 'object' ? deliveryStore?.source : null;

    // HIGHEST PRIORITY: Address set from ChangeBranchMenu (ALWAYS WINS)
    if (source === 'branchMenu' && storeAddress) {
      return storeAddress;
    }

    // Second priority: deliveryStore address (from deliveryDialog or other sources)
    // This takes precedence over user's default when explicitly set
    if (storeAddress && source) {
      return storeAddress;
    }

    // Third priority: user's default delivery address (from Firestore)
    if (userData?.defaultDeliveryAddress) {
      return userData.defaultDeliveryAddress;
    }

    // Fourth priority: deliveryStore address without source or deliveryAddress
    if (storeAddress || deliveryAddress) {
      return storeAddress || deliveryAddress;
    }

    // Fallback: first available address
    if (addresses && addresses.length > 0) {
      return addresses[0].id;
    }

    return '';
  }, [
    userData?.defaultDeliveryAddress,
    deliveryStore,
    deliveryAddress,
    addresses,
  ]);

  // Check if address was set from branch menu (has priority)
  const isAddressFromBranchMenu = useMemo(() => {
    return (
      typeof deliveryStore === 'object' &&
      deliveryStore?.source === 'branchMenu'
    );
  }, [deliveryStore]);

  // Get current delivery address object
  // Also check deliveryStore for the full address data (for temporary addresses)
  const currentDeliveryAddressData = useMemo(() => {
    if (!currentDeliveryAddressId) {
      return null;
    }

    // First try to find in addresses array
    if (addresses && addresses.length > 0) {
      const found = addresses.find(
        (addr) => addr.id === currentDeliveryAddressId
      );
      if (found) {
        return found;
      }
    }

    // For temporary addresses or when address not in array yet,
    // check if deliveryStore has the full data (deliveryStore is now an object)
    const storeAddressData =
      typeof deliveryStore === 'object' ? deliveryStore?.addressData : null;
    if (storeAddressData) {
      return {
        id: currentDeliveryAddressId,
        ...storeAddressData,
      };
    }

    return null;
  }, [currentDeliveryAddressId, addresses, deliveryStore]);

  /**
   * Convert location data (from Google Places) to address format
   * compatible with the delivery address system
   */
  const locationToAddressFormat = useCallback((locationData) => {
    if (!locationData) return null;

    return {
      lat: locationData.lat,
      lng: locationData.lng,
      city: locationData.city || '',
      state: locationData.state || '',
      country: locationData.country || '',
      zipCode: locationData.zipCode || '',
      placeId: locationData.placeId || '',
      displayName: locationData.displayName || '',
      streetNumber: locationData.streetNumber || '',
      route: locationData.route || '',
      address:
        locationData.displayName ||
        `${locationData.streetNumber || ''} ${locationData.route || ''}`.trim(),
    };
  }, []);

  /**
   * Save address to Firestore for ANY user (including anonymous)
   * Anonymous users can save addresses - they're stored under their anonymous uid
   * Returns the new address ID
   */
  const saveAddressToFirestore = useCallback(
    async (addressData, storeId = '', options = {}) => {
      const { isDisplayed = true } = options;
      // Check auth state at call time
      const uid = auth?.currentUser?.uid;

      if (!uid) {
        return null;
      }

      // NOTE: We now allow anonymous users to save addresses
      // Their addresses are stored under their anonymous uid
      // This matches the behavior of AddressHandlerDialog

      try {
        const addressRef = firestore
          .collection('users')
          .doc(uid)
          .collection('addresses')
          .doc();

        const newAddress = {
          ...addressData,
          id: addressRef.id,
          delivery_store_id: storeId,
          isDisplayed,
          createdAt: new Date().toISOString(),
          // Add a nickname based on the address for easier identification
          nickName: addressData.nickName || 'Saved Address',
        };

        await addressRef.set(newAddress);

        // Refresh addresses list
        const addressesSnapshot = await firestore
          .collection('users')
          .doc(uid)
          .collection('addresses')
          .get();

        const addressesList = [];
        addressesSnapshot.forEach((doc) => {
          addressesList.push({ id: doc.id, ...doc.data() });
        });

        dispatch(userAddresses(addressesList));

        return addressRef.id;
      } catch (error) {
        return null;
      }
    },
    [dispatch]
  );

  /**
   * Set an existing address as the delivery address
   * Works for both anonymous and logged-in users
   * @param addressId - The address ID to set
   * @param storeId - Optional store ID
   * @param source - Where the address was set from: 'branchMenu' | 'deliveryDialog' | null
   */
  const setDeliveryAddressById = useCallback(
    async (addressId, storeId = '', source = null) => {
      const uid = auth?.currentUser?.uid;

      // Find the address data from addresses array
      const addressData = addresses?.find((a) => a.id === addressId);

      // Update Redux state immediately with full address data and source
      dispatch(
        setDeliveryStore({
          address: addressId,
          store: storeId,
          addressData: addressData || null,
          source: source, // Track where address was set from
        })
      );

      // For logged-in users, also update Firestore (but don't update defaultDeliveryAddress
      // if source is branchMenu - we want to keep branchMenu address in Redux only)
      if (uid && !isAnonymous && source !== 'branchMenu') {
        try {
          await firestore.collection('users').doc(uid).set(
            {
              defaultDeliveryAddress: addressId,
            },
            { merge: true }
          );

          // Update Redux user data
          const updatedUser = {
            ...userData,
            defaultDeliveryAddress: addressId,
          };
          dispatch(setUser({ userData: updatedUser }));
        } catch (error) {
          // Failed to update default address
        }
      }

      return addressId;
    },
    [dispatch, userData, isAnonymous, addresses]
  );

  /**
   * Set delivery address from location data (from Google Places or GPS)
   * This is the key function for syncing branch filter address with delivery address
   *
   * @param locationData - Location object with lat, lng, displayName, etc.
   * @param storeId - Optional store ID for delivery
   * @param options - { saveToFirestore: boolean, setAsDefault: boolean, source: string, isDisplayed: boolean }
   */
  const setDeliveryAddressFromLocation = useCallback(
    async (locationData, storeId = '', options = {}) => {
      const {
        saveToFirestore = true,
        setAsDefault = true,
        source = null,
        isDisplayed = true,
      } = options;

      // IMPORTANT: Check auth state at call time, not from memoized value
      const uid = auth?.currentUser?.uid;
      const currentIsAnonymous = auth?.currentUser?.isAnonymous ?? true;

      if (!locationData || !locationData.lat || !locationData.lng) {
        return null;
      }

      const addressData = locationToAddressFormat(locationData);

      // Determine if we should save to Firestore
      // When saveToFirestore is false (e.g. branch menu search), never persist to Firestore
      const shouldSaveToFirestore = uid && saveToFirestore;

      // Use Redux-only (temp ID) when caller asked not to save or when no uid
      if (!shouldSaveToFirestore) {
        // Store in Redux only with a temporary ID
        const tempId = `temp_${Date.now()}`;

        // Store the full address data in a way that can be retrieved
        const tempAddress = {
          ...addressData,
          id: tempId,
          delivery_store_id: storeId,
          isTemporary: true,
        };

        // Dispatch with full address data so it's immediately available
        dispatch(
          setDeliveryStore({
            address: tempId,
            store: storeId,
            addressData: tempAddress,
            source: source, // Track where address was set from
          })
        );

        // Do NOT add temp to userAddresses: other flows (e.g. CartDrawer
        // getUserAddresses, syncs) must not see branch-menu-only addresses as
        // saved addresses, to avoid persisting them to Firestore or overwriting
        // the last saved address. Current address is read from deliveryStore
        // (addressData) when id is temp.

        return tempId;
      }

      // For logged-in users, check if this address already exists
      const existingAddress = addresses?.find(
        (addr) =>
          addr.lat === locationData.lat &&
          addr.lng === locationData.lng &&
          addr.displayName === locationData.displayName
      );

      if (existingAddress) {
        // Use existing address
        if (setAsDefault) {
          await setDeliveryAddressById(existingAddress.id, storeId, source);
        }
        return existingAddress.id;
      }

      // Save new address to Firestore
      const newAddressId = await saveAddressToFirestore(addressData, storeId, {
        isDisplayed,
      });

      if (newAddressId) {
        if (setAsDefault) {
          await setDeliveryAddressById(newAddressId, storeId, source);
        }
      }

      return newAddressId;
    },
    [
      dispatch,
      addresses,
      locationToAddressFormat,
      saveAddressToFirestore,
      setDeliveryAddressById,
    ]
  );

  /**
   * Clear the delivery address
   */
  const clearDeliveryAddress = useCallback(() => {
    dispatch(
      setDeliveryStore({
        address: '',
        store: '',
      })
    );
  }, [dispatch]);

  /**
   * Refresh addresses from Firestore
   */
  const refreshAddresses = useCallback(async () => {
    const uid = auth?.currentUser?.uid;
    const currentIsAnonymous = auth?.currentUser?.isAnonymous ?? true;

    if (!uid || currentIsAnonymous) {
      return;
    }

    try {
      const addressesSnapshot = await firestore
        .collection('users')
        .doc(uid)
        .collection('addresses')
        .get();

      const addressesList = [];
      addressesSnapshot.forEach((doc) => {
        addressesList.push({ id: doc.id, ...doc.data() });
      });

      dispatch(userAddresses(addressesList));
    } catch (error) {
      // Failed to refresh addresses
    }
  }, [dispatch]);

  /**
   * Ensure the current delivery address is saved to Firestore for logged-in users
   * This is called during order placement to guarantee the address is persisted
   * for both new and existing users. Returns the FINAL address ID to use in order.
   */
  const ensureDeliveryAddressSaved = useCallback(async () => {
    const uid = auth?.currentUser?.uid;
    const currentIsAnonymous = auth?.currentUser?.isAnonymous ?? true;

    // Only save for logged-in users
    if (!uid || currentIsAnonymous) {
      return currentDeliveryAddressId;
    }

    if (!currentDeliveryAddressData) {
      return currentDeliveryAddressId;
    }

    try {
      // Check if address already exists in Firestore (by coordinates)
      const existingAddressesSnapshot = await firestore
        .collection('users')
        .doc(uid)
        .collection('addresses')
        .where('displayName', '==', currentDeliveryAddressData.displayName)
        .where('lat', '==', currentDeliveryAddressData.lat || '')
        .where('lng', '==', currentDeliveryAddressData.lng || '')
        .limit(1)
        .get();

      if (existingAddressesSnapshot.size > 0) {
        const existingDoc = existingAddressesSnapshot.docs[0];
        const existingId = existingDoc.id;

        // If current ID is temporary, update Redux with the real ID
        if (currentDeliveryAddressId?.startsWith('temp_')) {
          dispatch(
            setDeliveryStore({
              address: existingId,
              store: deliveryStore?.store || '',
              addressData: existingDoc.data(),
              source: deliveryStore?.source,
            })
          );
        }

        return existingId;
      }

      // Save new address to Firestore - let Firestore generate the ID if current is temporary
      const isTemporaryId = currentDeliveryAddressId?.startsWith('temp_');
      let addressRef;
      let newAddressId;

      if (isTemporaryId) {
        // For temporary IDs, let Firestore generate a new one
        addressRef = firestore
          .collection('users')
          .doc(uid)
          .collection('addresses')
          .doc();
        newAddressId = addressRef.id;
      } else {
        // For real IDs, use the current one
        addressRef = firestore
          .collection('users')
          .doc(uid)
          .collection('addresses')
          .doc(currentDeliveryAddressId);
        newAddressId = currentDeliveryAddressId;
      }

      const addressToSave = {
        ...currentDeliveryAddressData,
        id: newAddressId,
        createdAt: new Date().toISOString(),
        nickName:
          currentDeliveryAddressData.nickName ||
          currentDeliveryAddressData.displayName ||
          'Saved Address',
      };

      await addressRef.set(addressToSave);

      // Update Redux with the new address ID if it changed
      if (newAddressId !== currentDeliveryAddressId) {
        dispatch(
          setDeliveryStore({
            address: newAddressId,
            store: deliveryStore?.store || '',
            addressData: addressToSave,
            source: deliveryStore?.source,
          })
        );
      }

      // Also set as default delivery address if not already set
      const userDoc = await firestore.collection('users').doc(uid).get();
      if (!userDoc.data()?.defaultDeliveryAddress) {
        await firestore.collection('users').doc(uid).set(
          {
            defaultDeliveryAddress: newAddressId,
          },
          { merge: true }
        );
      }

      // Refresh addresses list
      await refreshAddresses();

      return newAddressId;
    } catch (error) {
      // Don't throw - allow order to proceed even if address save fails
      return currentDeliveryAddressId;
    }
  }, [
    currentDeliveryAddressId,
    currentDeliveryAddressData,
    deliveryStore,
    dispatch,
    refreshAddresses,
  ]);

  return {
    // State
    currentDeliveryAddressId,
    currentDeliveryAddressData,
    addresses,
    isAnonymous,
    deliveryStore,
    isAddressFromBranchMenu, // True if address was set from ChangeBranchMenu

    // Actions
    setDeliveryAddressById,
    setDeliveryAddressFromLocation,
    saveAddressToFirestore,
    clearDeliveryAddress,
    refreshAddresses,
    ensureDeliveryAddressSaved, // Ensure address is saved before order placement

    // Utilities
    locationToAddressFormat,
  };
};

export default useDeliveryAddress;
